<script setup lang="ts">
    import { computed, onMounted, ref, watch, shallowReactive, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { Refresh } from '@browser/Browser'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog'
    import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue'
    import { guid } from '@common/Guid'
    import { MonthYear, MonthYearHelpers } from '@common/types'

    import { useSettingsPageQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'
    import { useCreateCorporateScenarioCommandV1, useUpdateCorporateScenarioCommandV1 } from '@Quest/CorporateScenarios/commands-vue-composables'
    import type { SettingsPageReadModel } from '@Quest/CorporateScenarios/readmodels'

    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => route.params.scenarioId ? String(route.params.scenarioId) : null)

    // ======================
    // Query
    // ======================

    const {
        runSettingsPageQueryV1,
        modelSettingsPageQueryV1
    } = useSettingsPageQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelSettingsPageQueryV1 as Ref<SettingsPageReadModel | null>
    const portfolios = computed(() => rm.value?.Portfolios ?? [])
    const createdScenarioId = ref<string | null>(null)

    // ======================
    // Commands
    // ======================

    const {
        invokeCreateCorporateScenarioCommandV1,
        succeedCreateCorporateScenarioCommandV1
    } = useCreateCorporateScenarioCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCorporateScenarioCommandV1,
        succeedUpdateCorporateScenarioCommandV1
    } = useUpdateCorporateScenarioCommandV1(workspaceId, strategyId)


    // ======================
    // Local State (stable instances)
    // ======================

    const planningStartDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const planningEndDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const benefitsConfidenceDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())

    const planningStartDate = computed<MonthYear | null>({
        get: () => rm.value?.PlanningStartDate ?? null,
        set: (v) => { if (rm.value) rm.value.PlanningStartDate = v }
    })

    const planningEndDate = computed<MonthYear | null>({
        get: () => rm.value?.PlanningEndDate ?? null,
        set: (v) => { if (rm.value) rm.value.PlanningEndDate = v }
    })

    const benefitsConfidenceDate = computed<MonthYear | null>({
        get: () => rm.value?.BenefitsConfidenceDate ?? null,
        set: (v) => { if (rm.value) rm.value.BenefitsConfidenceDate = v }
    })
    

    // ======================
    // Derived
    // ======================

    const planningStartDateLabel = computed(() =>
        MonthYearHelpers.toMonthYearLongString(planningStartDate.value)
    )

    const planningEndDateLabel = computed(() =>
        MonthYearHelpers.toMonthYearLongString(planningEndDate.value)
    )

    const benefitsConfidenceDateLabel = computed(() =>
        MonthYearHelpers.toMonthYearLongString(benefitsConfidenceDate.value)
    )


    // ======================
    // Month Pickers
    // ======================

    function showPlanningStartDateMonthPicker()
    {
        planningStartDateMonthPicker.show(planningStartDate.value, "Start of the Planning horizon", true)
    }

    function showPlanningEndDateMonthPicker()
    {
        planningEndDateMonthPicker.show(planningEndDate.value, "End of the Planning horizon", true)
    }

    function showBenefitsConfidenceDateMonthPicker()
    {
        benefitsConfidenceDateMonthPicker.show(benefitsConfidenceDate.value, "End of benefits confidence", false)
    }


    // ======================
    // Save
    // ======================

    function handleSavePortfolioScenarioSettings()
    {
        if (!rm.value || !planningStartDate.value || !planningEndDate.value)
            return

        const portfolioIds = rm.value.Portfolios
            .filter(p => p.IsConnected === true)
            .map(p => p.PortfolioId)

        if (!scenarioId.value)
        {
            const id = guid()
            createdScenarioId.value = id

            invokeCreateCorporateScenarioCommandV1({
                Id: createdScenarioId.value,
                Name: rm.value.ScenarioName,
                Description: rm.value.Description,
                PlanningStartDate: planningStartDate.value,
                PlanningEndDate: planningEndDate.value,
                Denomination: rm.value.Denomination,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                BenefitsConfidenceDate: benefitsConfidenceDate.value,
                PortfolioIds: portfolioIds
            })
        }
        else
        {
            invokeUpdateCorporateScenarioCommandV1({
                Id: scenarioId.value,
                Name: rm.value.ScenarioName,
                Description: rm.value.Description,
                PlanningStartDate: planningStartDate.value,
                PlanningEndDate: planningEndDate.value,
                Denomination: rm.value.Denomination,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                BenefitsConfidenceDate: benefitsConfidenceDate.value,
                PortfolioIds: portfolioIds
            })
        }
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Enterprise Plan - Settings"
        runSettingsPageQueryV1()
    })


    // ======================
    // Command Success
    // ======================

    watch(succeedCreateCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous && createdScenarioId.value)
        {
            location.href = `/#/enterprise-plan/portfolios/${workspaceId.value}/${strategyId.value}/${createdScenarioId.value}`
            createdScenarioId.value = null
        }
    })

    watch(succeedUpdateCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runSettingsPageQueryV1()
        }
    })
</script>

<template>
    <div>
        <MonthPickerDialog :viewModel="planningStartDateMonthPicker" @update-date="planningStartDate = $event" />
        <MonthPickerDialog :viewModel="planningEndDateMonthPicker" @update-date="planningEndDate = $event" />
        <MonthPickerDialog :viewModel="benefitsConfidenceDateMonthPicker" @update-date="benefitsConfidenceDate = $event" />

        <CorporateNavigation ActivePage="dashboard"></CorporateNavigation>

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6 v-if="scenarioId">
                        <a class="text-dark" v-sanitizedHref="`/#/enterprise-plan/portfolios/${workspaceId}/${strategyId}/${scenarioId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back to plan
                        </a>
                    </h6>
                    <h6 v-if="!scenarioId">
                        <a class="text-dark" v-sanitizedHref="`/#/enterprise/planning/${workspaceId}/${strategyId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back to plans
                        </a>
                    </h6>

                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Enterprise plan settings</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" class="btn btn-success" style="padding: 0.7em 2em;" form="scenarioSettings">
                        {{scenarioId ? 'Save changes' : 'Create plan'}}
                    </button>
                </div>
            </div>

            <hr />

            <form id="scenarioSettings" @submit.prevent="handleSavePortfolioScenarioSettings">
                <div class="form-group">
                    <label>Name:</label>
                    <input type="text" v-model.trim="rm.ScenarioName" class="form-control" required minlength="2" />
                </div>

                <div class="form-group">
                    <label>Description:</label>
                    <textarea v-model.trim="rm.Description" class="form-control" />
                </div>

                <div class="form-row">

                    <div class="form-group col">
                        <label for="denomination">Currency denomination:</label>
                        <select id="denomination" v-model="rm.Denomination" class="form-control">
                            <option value="Unit">Unit</option>
                            <option value="Thousand">Thousand</option>
                            <option value="Million">Million</option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group col">
                        <label>Planning from:</label>
                        <div class="input-group">
                            <input :value="planningStartDateLabel" type="text" class="form-control pointer bg-white" readonly @click="showPlanningStartDateMonthPicker" />
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary" type="button" @click="showPlanningStartDateMonthPicker">
                                    <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                        <div class="error">{{rm.PlanningStartDateError}}</div>
                    </div>

                    <div class="form-group col">
                        <label>Planning to:</label>
                        <div class="input-group">
                            <input :value="planningEndDateLabel" type="text" class="form-control pointer bg-white" readonly @click="showPlanningEndDateMonthPicker" />
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary" type="button" @click="showPlanningEndDateMonthPicker">
                                    <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                        <div class="error">{{rm.PlanningEndDateError}}</div>
                    </div>
                </div>

                <div class="form-row">

                    <div class="form-group col">
                        <label>Risk adjusted benefits:</label>
                        <select class="form-control" v-model="rm.ApplyBenefitsConfidenceFactor">
                            <option :value="true">Enabled</option>
                            <option :value="false">Disabled</option>
                        </select>
                    </div>

                    <div class="form-group col">
                        <label>End of benefits confidence:</label>
                        <div class="input-group">
                            <input :value="benefitsConfidenceDateLabel" type="text" class="form-control pointer bg-white" readonly @click="showBenefitsConfidenceDateMonthPicker" />
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary" type="button" @click="showBenefitsConfidenceDateMonthPicker">
                                    <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <hr class="my-4" />

            <h4></h4>

            <table class="table table-bordered table-hover">
                <thead>
                    <tr class="table-secondary">
                        <th colspan="2">Connected Portfolios</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="p in portfolios" :key="p.PortfolioId">
                        <td style="width: 80px;" class="text-center">
                            <a :class="p.IsConnected ? 'text-primary' : 'text-secondary'" @click="p.IsConnected = !p.IsConnected">
                                <i :class="p.IsConnected ? 'fa-toggle-on fa fa-2xl fa-solid' : 'fa-toggle-off fa fa-2xl fa-solid'"></i>
                            </a>
                        </td>
                        <td>
                            {{p.PortfolioName}}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
